import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

const SUBMIT_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noEntity: 'No current record available',
  noMethodology: 'No methodology to apply',
  unchanged: 'Functional usage did not change',
} as const

/**
 * Calls the API to apply the edited methodology for the current space lite
 * record.
 *
 * Cancels when the methodology ended up the same as what was loaded, so that
 * nothing is sent for an edit that was made and then undone.
 */
export const executeSubmit = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.request.observe().pipe(
      rxjs.withLatestFrom(
        feature.selectors.hasChanged.asObservable,
        feature.selectors.editedMethodology.asObservable,
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId()
          .asObservable,
      ),
      rxjs.mergeMap(
        ([action, hasChanged, methodology, context, spaceRecordId]) => {
          if (!hasChanged) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.unchanged }),
            )
          }

          if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noContext }),
            )
          }

          if (!ents.extra.space.Record.Id.isId(spaceRecordId)) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noEntity }),
            )
          }

          if (TypeChecker.isNullish(methodology)) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noMethodology }),
            )
          }

          return plugin.api
            .update({ context: { ...context, spaceRecordId }, methodology })
            .pipe(
              rxjs.map(response =>
                feature.actions.submit.success
                  .from(action)
                  .create({ response }),
              ),
              rxjs.catchError((error: Error) =>
                rxjs.of(
                  feature.actions.submit.error.from(action).create({ error }),
                ),
              ),
            )
        },
      ),
    ),
)
